"use client";

import React, { useState, useCallback } from "react";
import { ModalContainer, MODAL_IDS, ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@west-tennessee-consulting/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useUpdateRecurringCharge } from "@/lib/hooks/api/quickbooks/recurring-charges";
import type { RecurringCharge } from "@/lib/types/recurring-charges";
import type { ChangeScheduleModalData } from "@/lib/modals/data/modal-data-types";

const SCHEDULE_OPTIONS = [
  { value: "daily" as const, label: "Daily" },
  { value: "weekly" as const, label: "Weekly" },
  { value: "monthly" as const, label: "Monthly" },
] as const;

export function ChangeScheduleModal() {
  return (
    <ModalContainer<ChangeScheduleModalData> modalId={MODAL_IDS.recurringCharges.changeSchedule}>
      {({ isOpen, data: modalData, onClose }) => (
        <ChangeScheduleModalContent isOpen={isOpen} modalData={modalData} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

function ChangeScheduleModalContent({
  isOpen,
  modalData,
  onClose,
}: {
  isOpen: boolean;
  modalData: ChangeScheduleModalData | undefined;
  onClose: () => void;
}) {

  const [selectedSchedule, setSelectedSchedule] = useState<"daily" | "weekly" | "monthly" | "">("");
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: updateRecurringCharge } = useUpdateRecurringCharge();

  const handleClose = useCallback(() => {
    onClose();
    setSelectedSchedule("");
  }, [onClose]);

  const handleChangeSchedule = useCallback(async () => {
    if (!modalData || !selectedSchedule) {
      toast.error("Please select a schedule");
      return;
    }

    if (selectedSchedule === modalData.currentSchedule) {
      toast.info("Schedule is already set to this value");
      handleClose();
      return;
    }

    setIsLoading(true);
    try {
      await updateRecurringCharge({
        id: modalData.chargeId,
        schedule_type: selectedSchedule,
      });

      toast.success(`Schedule changed to ${selectedSchedule}`);
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to change schedule"
      );
    } finally {
      setIsLoading(false);
    }
  }, [modalData, selectedSchedule, updateRecurringCharge, handleClose]);

  if (!isOpen || !modalData) return null;

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.recurringCharges.changeSchedule}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Change Charge Schedule"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Change Charge Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Schedule */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              Current Schedule
            </Label>
            <p className="text-sm font-semibold capitalize">
              {modalData.currentSchedule}
            </p>
          </div>

          {/* New Schedule Selection */}
          <div className="space-y-2">
            <Label htmlFor="schedule" className="text-sm font-medium">
              New Schedule
            </Label>
            <Select 
              value={selectedSchedule} 
              onValueChange={(value) => setSelectedSchedule(value as "daily" | "weekly" | "monthly" | "")}
            >
              <SelectTrigger id="schedule" disabled={isLoading}>
                <SelectValue placeholder="Select a schedule" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {SCHEDULE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Confirmation Message */}
          {selectedSchedule && selectedSchedule !== modalData.currentSchedule && (
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/40 p-3">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                The charge schedule will be changed from{" "}
                <span className="font-semibold capitalize">
                  {modalData.currentSchedule}
                </span>{" "}
                to{" "}
                <span className="font-semibold capitalize">
                  {selectedSchedule}
                </span>
                .
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeSchedule}
              disabled={isLoading || !selectedSchedule}
              className="flex-1"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? "Updating..." : "Change Schedule"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </ResponsiveModal>
  );
}
